if (!customElements.get('product-modal')) {
  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const distance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
  const midpoint = (a, b) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });

  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      static CLICK_ZOOM = 2.5;
      static MAX_ZOOM = 4;

      constructor() {
        super();
        this.stage = this.querySelector('.product-lightbox__stage');
        this.status = this.querySelector('[data-lightbox-status]');
        this.index = 0;
        this.scale = 1;
        this.x = 0;
        this.y = 0;
        this.pointers = new Map();
        this.gesture = null;
        this.pinch = null;
        this.lastTap = 0;

        this.querySelector('[data-lightbox-prev]').addEventListener('click', () => this.go(this.index - 1));
        this.querySelector('[data-lightbox-next]').addEventListener('click', () => this.go(this.index + 1));
        this.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowLeft') this.go(this.index - 1);
          if (event.key === 'ArrowRight') this.go(this.index + 1);
        });

        this.stage.addEventListener('pointerdown', this.onPointerDown.bind(this));
        this.stage.addEventListener('pointermove', this.onPointerMove.bind(this));
        this.stage.addEventListener('pointerup', this.onPointerUp.bind(this));
        this.stage.addEventListener('pointercancel', this.onPointerCancel.bind(this));
        this.stage.addEventListener('wheel', this.onWheel.bind(this), { passive: false });
        window.addEventListener('resize', () => this.hasAttribute('open') && this.apply(false));
      }

      // Slides are re-queried because product-info.js replaces the stage's innerHTML on variant change
      get slides() {
        return Array.from(this.stage.querySelectorAll('.product-lightbox__slide'));
      }

      get image() {
        return this.slides[this.index]?.querySelector('img');
      }

      show(opener) {
        super.show(opener);
        const mediaId = opener?.getAttribute('data-media-id');
        const index = this.slides.findIndex((slide) => slide.dataset.mediaId === mediaId);
        this.go(Math.max(index, 0));
      }

      hide() {
        super.hide();
        this.pointers.clear();
        this.gesture = null;
        this.pinch = null;
        this.resetZoom(false);
      }

      go(index) {
        const slides = this.slides;
        const count = slides.length;
        if (!count) return;

        this.resetZoom(false);
        this.index = (index + count) % count;
        slides.forEach((slide, i) => slide.classList.toggle('is-active', i === this.index));
        [this.index - 1, this.index + 1].forEach((i) => {
          const img = slides[(i + count) % count].querySelector('img');
          if (img) img.loading = 'eager';
        });

        this.toggleAttribute('data-single', count < 2);
        this.status.textContent = `Image ${this.index + 1} of ${count}`;
      }

      onPointerDown(event) {
        if (event.pointerType === 'mouse') return;

        this.stage.setPointerCapture(event.pointerId);
        this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

        if (this.pointers.size === 2) {
          const [a, b] = this.pointers.values();
          this.gesture = null;
          this.pinch = { distance: distance(a, b), mid: midpoint(a, b), scale: this.scale, x: this.x, y: this.y };
        } else if (this.pointers.size === 1) {
          this.startGesture(event.clientX, event.clientY, false);
        }
      }

      onPointerMove(event) {
        if (event.pointerType === 'mouse') {
          if (this.scale > 1) this.followPointer(event.clientX, event.clientY);
          return;
        }
        if (!this.pointers.has(event.pointerId)) return;
        this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

        if (this.pinch && this.pointers.size === 2) {
          const [a, b] = this.pointers.values();
          const scale = clamp((this.pinch.scale * distance(a, b)) / this.pinch.distance, 1, ProductModal.MAX_ZOOM);
          const mid = midpoint(a, b);
          const center = this.center();
          const ratio = scale / this.pinch.scale;
          this.scale = scale;
          this.x = mid.x - center.x - ratio * (this.pinch.mid.x - center.x - this.pinch.x);
          this.y = mid.y - center.y - ratio * (this.pinch.mid.y - center.y - this.pinch.y);
          this.loadHiRes();
          this.apply(false);
          return;
        }

        const gesture = this.gesture;
        if (!gesture) return;
        const dx = event.clientX - gesture.startX;
        const dy = event.clientY - gesture.startY;
        if (Math.abs(dx) > 6 || Math.abs(dy) > 6) gesture.moved = true;

        if (this.scale > 1) {
          this.x = gesture.x + dx;
          this.y = gesture.y + dy;
          this.apply(false);
        } else if (this.image) {
          // Swipe preview: let the image follow the finger horizontally
          this.image.style.transition = 'none';
          this.image.style.transform = `translate3d(${dx}px, 0, 0)`;
        }
      }

      onPointerUp(event) {
        if (event.pointerType === 'mouse') {
          if (event.button === 0) this.onMouseClick(event);
          return;
        }
        if (!this.pointers.has(event.pointerId)) return;
        this.pointers.delete(event.pointerId);

        if (this.pinch) {
          if (this.pointers.size === 2) return;
          this.pinch = null;
          if (this.scale < 1.05) this.resetZoom(true);
          else this.apply(true);
          // Continue panning with the finger that is still down
          const remaining = this.pointers.values().next().value;
          if (remaining) this.startGesture(remaining.x, remaining.y, true);
          return;
        }

        const gesture = this.gesture;
        this.gesture = null;
        if (!gesture) return;
        const dx = event.clientX - gesture.startX;
        const dy = event.clientY - gesture.startY;

        if (!gesture.moved) {
          this.onTap(event.clientX, event.clientY);
        } else if (this.scale === 1 && Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
          this.go(this.index + (dx < 0 ? 1 : -1));
        } else {
          this.apply(true);
        }
      }

      onPointerCancel(event) {
        this.pointers.delete(event.pointerId);
        this.gesture = null;
        this.pinch = null;
        if (this.scale < 1.05) this.resetZoom(true);
        else this.apply(true);
      }

      onMouseClick(event) {
        if (event.target !== this.image) {
          // Click on the empty area around the image closes the lightbox
          if (this.scale === 1) this.hide();
          return;
        }
        if (this.scale > 1) {
          this.resetZoom(true);
        } else {
          this.zoomTo(ProductModal.CLICK_ZOOM, event.clientX, event.clientY);
          this.followPointer(event.clientX, event.clientY);
        }
      }

      onTap(clientX, clientY) {
        const now = Date.now();
        if (now - this.lastTap < 300) {
          this.lastTap = 0;
          if (this.scale > 1) this.resetZoom(true);
          else this.zoomTo(ProductModal.CLICK_ZOOM, clientX, clientY);
        } else {
          this.lastTap = now;
        }
      }

      onWheel(event) {
        event.preventDefault();
        const scale = clamp(this.scale * Math.exp(-event.deltaY * 0.0015), 1, ProductModal.MAX_ZOOM);
        if (scale === 1) this.resetZoom(true);
        else this.zoomTo(scale, event.clientX, event.clientY, false);
      }

      startGesture(x, y, moved) {
        this.gesture = { startX: x, startY: y, x: this.x, y: this.y, moved };
      }

      // Zoom to `scale`, keeping the screen point (clientX, clientY) fixed under the cursor/finger
      zoomTo(scale, clientX, clientY, animate = true) {
        const center = this.center();
        const ratio = scale / this.scale;
        this.x = clientX - center.x - ratio * (clientX - center.x - this.x);
        this.y = clientY - center.y - ratio * (clientY - center.y - this.y);
        this.scale = scale;
        this.loadHiRes();
        this.apply(animate);
      }

      // Desktop: pan so the part of the image under the cursor stays in view
      followPointer(clientX, clientY) {
        const rect = this.stage.getBoundingClientRect();
        const { maxX, maxY } = this.bounds();
        this.x = maxX * (1 - (2 * (clientX - rect.left)) / rect.width);
        this.y = maxY * (1 - (2 * (clientY - rect.top)) / rect.height);
        this.apply(true);
      }

      resetZoom(animate) {
        this.scale = 1;
        this.x = 0;
        this.y = 0;
        this.apply(animate);
      }

      apply(animate) {
        const image = this.image;
        if (!image) return;
        const { maxX, maxY } = this.bounds();
        this.x = clamp(this.x, -maxX, maxX);
        this.y = clamp(this.y, -maxY, maxY);
        image.style.transition = animate ? '' : 'none';
        image.style.transform = `translate3d(${this.x}px, ${this.y}px, 0) scale(${this.scale})`;
        this.classList.toggle('is-zoomed', this.scale > 1);
      }

      bounds() {
        const image = this.image;
        if (!image) return { maxX: 0, maxY: 0 };
        return {
          maxX: Math.max(0, (image.offsetWidth * this.scale - this.stage.clientWidth) / 2),
          maxY: Math.max(0, (image.offsetHeight * this.scale - this.stage.clientHeight) / 2),
        };
      }

      center() {
        const rect = this.stage.getBoundingClientRect();
        return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
      }

      loadHiRes() {
        const image = this.image;
        if (!image || !image.dataset.zoomSrc || image.dataset.zoomLoaded) return;
        image.dataset.zoomLoaded = 'true';
        const hiRes = new Image();
        hiRes.src = image.dataset.zoomSrc;
        hiRes
          .decode()
          .then(() => {
            image.removeAttribute('srcset');
            image.src = hiRes.src;
          })
          .catch(() => {});
      }
    }
  );
}
